<template>
  <div class="ts-app ts-modal-root">
    <div
      :id="id"
      ref="panel"
      class="offcanvas"
      :class="[`offcanvas-${placement}`, panelClass, { show: isOpen }]"
      tabindex="-1"
      role="dialog"
      :aria-modal="isOpen ? 'true' : null"
      :aria-hidden="isOpen ? null : 'true'"
      :aria-labelledby="labelledby"
      @keydown.esc="close"
    >
      <slot :close="close" :visible="isOpen" />
    </div>
    <div v-if="isOpen" class="offcanvas-backdrop fade show" @click="close"></div>
  </div>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';

// BootstrapVue's collapse/sidebar protocol on $root, so v-b-toggle.<id> and
// $root.$emit('bv::toggle::collapse', id) keep working for every panel.
const ROOT_TOGGLE = 'bv::toggle::collapse';
const ROOT_REQUEST_STATE = 'bv::request-state::collapse';
const ROOT_STATE = 'bv::collapse::state';
const ROOT_SYNC_STATE = 'bv::collapse::sync-state';

// Bootstrap 5 offcanvas driven by Vue state instead of bootstrap.js. Works with v-model
// (controlled) or on its own, opened by v-b-toggle like a b-sidebar. The root is moved to
// <body> so the panel is never clipped by a parent, like the design's BodyPortal.
@Component
export default class TsOffcanvas extends Vue {
  @Prop({ required: true }) readonly id!: string;

  // null: not bound with v-model, the panel keeps its own state
  @Prop({ default: null }) readonly value!: boolean | null;

  @Prop({ default: 'end' }) readonly placement!: 'start' | 'end' | 'top' | 'bottom';

  @Prop({ default: '' }) readonly panelClass!: string;

  @Prop({ default: null }) readonly labelledby!: string | null;

  // Like b-sidebar: a navigation closes the panel unless it should stay open (e.g. live filters)
  @Prop({ type: Boolean, default: false }) readonly noCloseOnRouteChange!: boolean;

  localOpen = false;

  previousFocus: HTMLElement | null = null;

  get isOpen() {
    return this.value === null ? this.localOpen : this.value;
  }

  created() {
    this.$root.$on(ROOT_TOGGLE, this.onRootToggle);
    this.$root.$on(ROOT_REQUEST_STATE, this.onRequestState);
  }

  mounted() {
    document.body.appendChild(this.$el);
  }

  beforeDestroy() {
    this.$root.$off(ROOT_TOGGLE, this.onRootToggle);
    this.$root.$off(ROOT_REQUEST_STATE, this.onRequestState);
    if (this.isOpen) document.body.classList.remove('overflow-hidden');
    this.$el.remove();
  }

  @Watch('isOpen')
  onToggle(open: boolean) {
    document.body.classList.toggle('overflow-hidden', open);
    this.$root.$emit(ROOT_STATE, this.id, open);
    this.$emit('change', open);
    if (open) {
      this.previousFocus = document.activeElement as HTMLElement | null;
      this.$nextTick(() => {
        (this.$refs.panel as HTMLElement).focus();
        this.$emit('shown');
      });
    } else {
      this.previousFocus?.focus();
      this.$emit('hidden');
    }
  }

  @Watch('$route')
  onRouteChange() {
    if (!this.noCloseOnRouteChange && this.isOpen) this.close();
  }

  setOpen(open: boolean) {
    this.localOpen = open;
    this.$emit('input', open);
  }

  open() {
    this.setOpen(true);
  }

  close() {
    this.setOpen(false);
  }

  onRootToggle(id: string) {
    if (id === this.id) this.setOpen(!this.isOpen);
  }

  // A v-b-toggle button asks for the state to set its aria-expanded
  onRequestState(id: string) {
    if (id === this.id) this.$root.$emit(ROOT_SYNC_STATE, this.id, this.isOpen);
  }
}
</script>
